import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { getLocation } from '$utilities/location';
import { useLocale } from '$contexts/locale.jsx';
import SearchDialog from './search-dialog';
import Button from '$partials/ui/button';
import IconSearch from '$partials/icons/search';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const MobileNavigation = ({
    menu,
    search
}) => {
    const { current: locale, options: localeOptions, translate: t, formatURI } = useLocale();
    const [isOpen, setIsOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    const dialogRef = useRef();
    const location = getLocation();

    const close = () => {
        if (dialogRef.current) {
            dialogRef.current.close();
        }
        clearAllBodyScrollLocks();
        setIsOpen(false);
    };

    const open = () => {
        if (dialogRef.current) {
            disableBodyScroll(dialogRef.current);
            dialogRef.current.showModal();
        }
        setIsOpen(true);
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') { close(); }
        };

        window.addEventListener('keydown', handleKeyDown, { passive: true });

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <>
            <Button
                className='inline-flex mr-40'
                onClick={() => setSearchOpen(!searchOpen)}
                renderIcon={<IconSearch stroke='currentColor'/>}
                type='button' />

            <button
                onClick={open}
                type='button'>
                <svg
                    style={{ width: '24px', height: '18px' }}
                    viewBox='0 0 24 18'
                    xmlns='http://www.w3.org/2000/svg'>
                    <path
                        d='M24 0.324951H0V1.67495H24V0.324951Z'
                        fill='currentColor' />
                    <path
                        d='M24 8.32495H0V9.67495H24V8.32495Z'
                        fill='currentColor' />
                    <path
                        d='M24 16.325H0V17.6749H24V16.325Z'
                        fill='currentColor' />
                </svg>
            </button>

            <SearchDialog
                closeModal={setSearchOpen}
                isModalOpen={searchOpen}
                keywords={search?.keywords}
                tags={search?.trending}/>

            <dialog
                className='fixed top-0 left-0 w-full h-full max-w-none max-h-none bg-orange text-white text-left m-0'
                inert={isOpen ? null : ''}
                ref={dialogRef}>
                <div className='layout-container'>
                    <div className='h-full py-60 px-10 sm:px-20'>
                        {/* Close */}
                        <div className='text-right mb-40'>
                            <button
                                aria-label='Close'
                                onClick={close}
                                type='button'>
                                <svg
                                    style={{ width: '18px', height: '18px' }}
                                    viewBox='0 0 18 18'
                                    xmlns='http://www.w3.org/2000/svg'>
                                    <path
                                        d='M16.9706 -0.000103474L0 16.9705L0.954594 17.9251L17.9252 0.954491L16.9706 -0.000103474Z'
                                        fill='white' />
                                    <path
                                        d='M17.9627 17.0082L0.992188 0.0375977L0.0375931 0.992192L17.0082 17.9628L17.9627 17.0082Z'
                                        fill='white' />
                                </svg>
                            </button>
                        </div>

                        {/* Main links */}
                        <ul className='list-none type-style-2 mb-80'>
                            {menu.navigationLinks.items.map((navLink, idx) => {
                                const title = navLink.title;
                                let url;
                                if (navLink.__typename === 'BvaMagazineAwardsNomineesPage') {
                                    url = formatURI(`/year-in-sport/${navLink.slug}/nominees`);
                                } else if (navLink.__typename === 'BvaMagazineAwardsLandingPage') {
                                    url = formatURI(`/year-in-sport/${navLink.slug}/winners`);
                                } else {
                                    url = formatURI(`/${navLink.slug}`);
                                }

                                return (
                                    <li
                                        className='mb-30'
                                        key={idx}>
                                        <a href={url}>
                                            {title}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>

                        {/* Locales */}
                        <p className='type-style-7 font-semibold uppercase mb-15 opacity-80'>{t('Languages')}</p>

                        <ul className='flex flex-wrap list-none mb-80 -mx-10'>
                            {localeOptions.map((option, i) => {
                                const isCurrent = option?.code === locale?.code;

                                return (
                                    <li
                                        className='w-1/2 mb-10 px-10'
                                        key={i}>
                                        <a
                                            className={`inline-block py-10 ${isCurrent ? ' bg-white text-orange px-10' : ''} rounded font-semibold`}
                                            href={formatURI(location.pathname, option?.code)}>
                                            {option.labelLong}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>

                        {/* Sign up */}
                        <p className='type-style-7 font-semibold uppercase mb-15 opacity-80'>{t('Get Started')}</p>

                        <ul className='list-none'>
                            <li>
                                <a
                                    className='type-style-7 font-semibold'
                                    href='https://strava.app.link/N9u5AndPPMb'
                                    rel='noreferrer'
                                    target='_blank'>
                                    {t('Sign up')}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </dialog>
        </>
    );
};

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

const propTypes = {
    menu: PropTypes.shape({
        navigationLinks: PropTypes.object,
    }),
    search: PropTypes.shape({
        trending: PropTypes.array,
        keywords: PropTypes.array,
    }),
};

MobileNavigation.propTypes = propTypes;

export default MobileNavigation;
